// src/pages/Consultation/SessionDetail/SessionDetail.jsx - WITH ORDER REVIEW TAB
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiArrowLeft,
  FiShoppingCart,
  FiDollarSign,
  FiRefreshCw,
  FiClock,
  FiInfo,
  FiCheckCircle, // ← NEW: For Review tab icon
} from "react-icons/fi";
import toast from "react-hot-toast";

// Components
import SessionHeader from "./SessionHeader";
import Button from "../../../components/common/Button";

// Tabs
import CartTab from "./Tabs/CartTab";
import PricingTab from "./Tabs/PricingTab";
import ExchangeTab from "./Tabs/ExchangeTab";
import TimelineTab from "./Tabs/TimelineTab";
import DetailsTab from "./Tabs/DetailsTab";
import OrderReviewTab from "./Tabs/OrderReviewTab"; // ← NEW: Import OrderReviewTab

// Modals
import PauseSessionModal from "./Modals/PauseSessionModal";
import CompleteSessionModal from "./Modals/CompleteSessionModal";
import PriceAdjustmentModal from "./Modals/PriceAdjustmentModal";
import ExchangeItemModal from "./Modals/ExchangeItemModal";
import OrderSuccessModal from "./Modals/OrderSuccessModal"; // ← NEW: Import OrderSuccessModal

// Redux Thunks
import {
  calculatePricingThunk,
  getSessionThunk,
  resumeSessionThunk,
  updateSessionNotesThunk,
} from "../../../store/thunks/consultationThunks";

const SessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux State
  const { currentSession, sessionCart, sessionPricing, loadingSession, error } =
    useSelector((state) => state.consultation);

  const { user } = useSelector((state) => state.auth);
  const adminInfo = {
    adminCode: user?.adminCode || user?._id,
    adminName: user?.name,
    adminId: user?._id,
    role: user?.role,
  };

  // Local State - Tabs
  const [activeTab, setActiveTab] = useState("cart");

  // Local State - Modals
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isPriceAdjustmentModalOpen, setIsPriceAdjustmentModalOpen] =
    useState(false);
  const [isExchangeItemModalOpen, setIsExchangeItemModalOpen] = useState(false);

  // ← NEW: Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  // Local State - Actions
  const [resumingSession, setResumingSession] = useState(false);
  const [refreshingSession, setRefreshingSession] = useState(false);

  // Fetch session on mount
  useEffect(() => {
    if (sessionId) {
      dispatch(getSessionThunk(sessionId));
    }
  }, [sessionId, dispatch]);

  useEffect(() => {
    if (sessionCart?.items?.length > 0 && !sessionPricing) {
      dispatch(calculatePricingThunk(sessionId));
    }
  }, [sessionCart, sessionPricing, sessionId, dispatch]);

  // Handle resume session
  const handleResumeSession = async () => {
    if (resumingSession) return;

    setResumingSession(true);

    try {
      await dispatch(
        resumeSessionThunk({
          sessionId: currentSession._id,
          adminCode: adminInfo.adminCode,
          adminName: adminInfo.adminName,
        })
      ).unwrap();

      toast.success("Session resumed successfully");
    } catch (error) {
      console.error("Resume session error:", error);
      toast.error(error || "Failed to resume session");
    } finally {
      setResumingSession(false);
    }
  };

  // Handle refresh session
  const handleRefreshSession = async () => {
    if (refreshingSession) return;

    setRefreshingSession(true);

    try {
      await dispatch(getSessionThunk(sessionId)).unwrap();
      toast.success("Session refreshed");
    } catch (error) {
      console.error("Refresh session error:", error);
      toast.error("Failed to refresh session");
    } finally {
      setRefreshingSession(false);
    }
  };

  const handleUpdateNotes = async (notes) => {
    try {
      await dispatch(
        updateSessionNotesThunk({
          sessionId: currentSession._id,
          adminNotes: notes,
          adminCode: adminInfo.adminCode,
          adminName: adminInfo.adminName,
        })
      ).unwrap();

      toast.success("Notes updated successfully");
      return true;
    } catch (error) {
      console.error("Update notes error:", error);
      toast.error(error || "Failed to update notes");
      throw new Error(error || "Failed to update notes");
    }
  };

  // ← NEW: Handle order success
  const handleOrderSuccess = (result) => {
    setOrderResult(result);
    setShowSuccessModal(true);
  };

  // ← NEW: Post-order actions
  const handleViewOrder = () => {
    if (orderResult?.data?.order?._id) {
      navigate(`/orders/${orderResult.data.order._id}`);
    }
    setShowSuccessModal(false);
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleStartNewSession = () => {
    setShowSuccessModal(false);
    navigate("/consultation/start-session");
  };

  // Handle back navigation
  const handleBack = () => {
    navigate("/consultation");
  };

  // Loading State
  if (loadingSession && !currentSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading session...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error && !currentSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Session Not Found
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={handleBack}>
            <FiArrowLeft className="mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // No Session State
  if (!currentSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">No session data available</p>
          <Button onClick={handleBack} className="mt-4">
            <FiArrowLeft className="mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Calculate cart summary
  const cartItemCount = sessionCart?.items?.length || 0;
  const hasCart = cartItemCount > 0;
  const isConverted = currentSession.outcome?.converted;

  // ✅ FIXED: Check if can create order
  const hasExchangeItems = (currentSession.exchangeItems?.length || 0) > 0;
  const hasItemsToOrder = hasCart || hasExchangeItems; // Products OR Exchange

  const canCreateOrder =
    (hasItemsToOrder && // ✅ Now includes exchange-only orders
      sessionPricing &&
      ["ACTIVE", "PAUSED"].includes(currentSession.status)) ||
    isConverted;
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Back Button & Title */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiArrowLeft className="text-xl" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {currentSession.sessionCode}
                </h1>
                <p className="text-sm text-gray-500">
                  {currentSession.sessionType} • {currentSession.branchCode}
                </p>
              </div>
            </div>

            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshSession}
              loading={refreshingSession}
              disabled={refreshingSession}
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-6">
        {/* Session Header */}
        <SessionHeader
          session={currentSession}
          onPause={() => setIsPauseModalOpen(true)}
          onResume={handleResumeSession}
          onComplete={() => setIsCompleteModalOpen(true)}
          resumingSession={resumingSession}
        />

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <QuickStatCard
            icon={FiShoppingCart}
            label="Cart Items"
            value={cartItemCount}
            color="blue"
          />
          <QuickStatCard
            icon={FiDollarSign}
            label="Cart Total"
            value={`₹${
              sessionPricing?.summary?.grandTotal?.toLocaleString() || 0
            }`}
            color="green"
          />
          <QuickStatCard
            icon={FiRefreshCw}
            label="Exchange Items"
            value={currentSession.exchangeItems?.length || 0}
            color="purple"
          />
          <QuickStatCard
            icon={FiClock}
            label="Duration"
            value={formatDuration(currentSession.duration)}
            color="orange"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          {/* Tab Headers */}
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto">
              {!isConverted && (
                <>
                  <TabButton
                    active={activeTab === "cart"}
                    onClick={() => setActiveTab("cart")}
                    icon={FiShoppingCart}
                    label="Cart"
                    badge={cartItemCount}
                  />
                  <TabButton
                    active={activeTab === "pricing"}
                    onClick={() => setActiveTab("pricing")}
                    icon={FiDollarSign}
                    label="Pricing"
                  />
                  <TabButton
                    active={activeTab === "exchange"}
                    onClick={() => setActiveTab("exchange")}
                    icon={FiRefreshCw}
                    label="Exchange"
                    badge={currentSession.exchangeItems?.length}
                  />
                </>
              )}
              <TabButton
                active={activeTab === "timeline"}
                onClick={() => setActiveTab("timeline")}
                icon={FiClock}
                label="Timeline"
              />
              <TabButton
                active={activeTab === "details"}
                onClick={() => setActiveTab("details")}
                icon={FiInfo}
                label="Details"
              />

              {/* ← NEW: Review & Place Order Tab */}
              <TabButton
                active={activeTab === "review"}
                onClick={() => setActiveTab("review")}
                icon={FiCheckCircle}
                label="Review & Place"
                enabled={canCreateOrder}
                highlight={true}
              />
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {!isConverted && activeTab === "cart" && (
              <CartTab
                session={currentSession}
                cartItems={sessionCart?.items || []}
                adminInfo={adminInfo}
                onAddAdjustment={() => setIsPriceAdjustmentModalOpen(true)}
              />
            )}

            {!isConverted && activeTab === "pricing" && (
              <PricingTab
                session={currentSession}
                sessionPricing={sessionPricing}
                cartItems={sessionCart?.items || []}
                exchangeItems={currentSession.exchangeItems || []}
                onAddAdjustment={() => setIsPriceAdjustmentModalOpen(true)}
                adminInfo={adminInfo}
              />
            )}

            {!isConverted && activeTab === "exchange" && (
              <ExchangeTab
                session={currentSession}
                adminInfo={adminInfo}
                onAddExchangeItem={() => setIsExchangeItemModalOpen(true)}
              />
            )}

            {activeTab === "timeline" && (
              <TimelineTab session={currentSession} />
            )}

            {activeTab === "details" && (
              <DetailsTab
                session={currentSession}
                onUpdateNotes={handleUpdateNotes}
              />
            )}

            {/* ← NEW: Review & Place Order Tab Content */}
            {activeTab === "review" && (
              <OrderReviewTab
                session={currentSession}
                sessionPricing={sessionPricing}
                cartItems={sessionCart?.items || []}
                exchangeItems={currentSession.exchangeItems || []}
                customer={currentSession.userId}
                adminInfo={adminInfo}
                onOrderSuccess={handleOrderSuccess}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <PauseSessionModal
        isOpen={isPauseModalOpen}
        onClose={() => setIsPauseModalOpen(false)}
        session={currentSession}
        adminInfo={adminInfo}
      />

      <CompleteSessionModal
        isOpen={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
        session={currentSession}
        adminInfo={adminInfo}
        hasCart={hasCart}
      />

      <PriceAdjustmentModal
        isOpen={isPriceAdjustmentModalOpen}
        onClose={() => setIsPriceAdjustmentModalOpen(false)}
        session={currentSession}
        adminInfo={adminInfo}
        cartItems={sessionCart?.items || []}
        exchangeItems={currentSession.exchangeItems || []}
      />

      <ExchangeItemModal
        isOpen={isExchangeItemModalOpen}
        onClose={() => setIsExchangeItemModalOpen(false)}
        session={currentSession}
        adminInfo={adminInfo}
      />

      {/* ← NEW: Order Success Modal */}
      <OrderSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        orderData={orderResult?.data}
        sessionCode={currentSession?.sessionCode}
        onViewOrder={handleViewOrder}
        onPrintReceipt={handlePrintReceipt}
        onStartNewSession={handleStartNewSession}
      />
    </div>
  );
};

// Helper Components

// Tab Button Component - UPDATED to support enabled/disabled and highlight
const TabButton = ({
  active,
  onClick,
  icon: Icon,
  label,
  badge,
  enabled = true,
  highlight = false,
}) => (
  <button
    onClick={onClick}
    disabled={!enabled}
    className={`relative flex items-center gap-2 px-5 py-3 font-medium border-b-2 rounded-t-lg transition-all duration-300 whitespace-nowrap
      ${!enabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
      ${
        active
          ? "border-primary text-primary bg-gradient-to-t from-primary-light/10 to-white shadow-sm"
          : "border-transparent text-gray-600 hover:text-primary hover:bg-gradient-to-t hover:from-neutral-light/50 hover:to-white"
      }
      ${highlight && enabled ? "bg-green-50 hover:bg-green-100" : ""}
    `}
  >
    <Icon className="text-base" />
    <span>{label}</span>

    {badge !== undefined && badge > 0 && (
      <span
        className={`px-2 py-0.5 text-xs font-semibold rounded-full transition-colors duration-300 ${
          active
            ? "bg-secondary text-white"
            : "bg-gray-200 text-gray-700 hover:bg-secondary-light hover:text-white"
        }`}
      >
        {badge}
      </span>
    )}
  </button>
);

// Quick Stat Card Component
const QuickStatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-orange-50 text-orange-600",
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="text-xl" />
        </div>
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className="text-lg font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

// Format duration helper
const formatDuration = (minutes) => {
  if (!minutes) return "0 min";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

export default SessionDetail;
